import { Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { formatDate } from "app/util/date/tools/common"
import { getCurrencySymbol } from "form/inventory/tools/common"
import { PageLoading } from "page/components/PageLoading"
import { IPayment } from "payment/model"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { getDataGridTexts } from "ui/tools/list"

interface IMyAgencyPaymentListProps {
  payments: IPayment[] | null
  errorMessage: string
}

export const MyAgencyPaymentList = React.memo(({ payments, errorMessage }: IMyAgencyPaymentListProps): JSX.Element => {
  const columns: Array<GridColDef<IPayment>> = [
    {
      field: "initiator_email",
      headerName: translate("label.email"),
      flex: 4
    },
    {
      field: "created_at",
      headerName: translate("label.creationDate"),
      flex: 2,
      renderCell: params => formatDate(new Date(params.row.created_at), { displayTime: true })
    },
    {
      field: "amount",
      headerName: translate("label.amount"),
      flex: 2,
      renderCell: ({ row }) => `${row.amount} ${getCurrencySymbol(row.agency_currency)}`
    },
    {
      field: "tax",
      headerName: translate("label.tax"),
      flex: 2,
      renderCell: ({ row }) => `${row.tax} ${getCurrencySymbol(row.agency_currency)}`
    },
    {
      field: "total",
      headerName: translate("label.total"),
      flex: 2,
      renderCell: ({ row }) => `${row.total} ${getCurrencySymbol(row.agency_currency)}`
    }
  ]
  return (
    <FlexBox vertical fullWidth gap>
      {!errorMessage && payments === null && <PageLoading title={translate("label.loading.data")} />}
      {errorMessage ? (
        <Typography color="error" fontSize=".9rem">
          {errorMessage}
        </Typography>
      ) : (
        <FlexBox
          fullWidth
          vertical
          sx={{
            "position": "relative",
            "height": "26rem",
            "minHeight": "26rem",
            "maxHeight": "26rem",
            "transition": "all 400ms ease",
            "& > *": {
              width: "100%"
            },
            "& .MuiDataGrid-selectedRowCount": {
              visibility: "hidden"
            },
            "& .MuiDataGrid-root": {
              border: "none"
            },
            "& .MuiDataGrid-cell": {
              fontSize: ".8rem",
              fontWeight: 300
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer"
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "inherit"
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
              {
                outline: "none !important"
              }
          }}>
          <Typography color="textPrimary" fontSize=".9rem">
            {translate("agency.payment.list.title")}
          </Typography>
          <DataGrid
            rows={payments || []}
            columns={columns}
            className="my-agency-payments"
            rowHeight={30}
            autoPageSize
            initialState={{
              sorting: { sortModel: [{ field: "creationDate", sort: "desc" }] }
            }}
            disableSelectionOnClick
            localeText={getDataGridTexts("agency.payment.list.no.row")}
          />
        </FlexBox>
      )}
    </FlexBox>
  )
})
